import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Chip,
  Divider,
  Grid2,
  Paper,
  Tab,
  Tabs,
  useTheme,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import dayjs, { Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { EmployeeRoles } from "../../../models/Employees/EmployeeRoles";
import PermissionBlockLabel from "../../components/common/pop-up/PermissionBlockLabel ";
import CustomTypography from "../../components/common/form/CustomTypography";
import { DateRangePickerNew } from "../../components/common/textFields/DatePickerRangeNew";
import AppSearchDropdownMulti from "../../components/common/filter/AppSearchDropdownMulti";
import AppButton from "../../components/common/buttons/AppButton";
import LoaderOverlay from "../../components/common/pages/AppLoader";
import { useNotification } from "../../../utils/NotificationsContext";
import { SortType } from "../../../models/SortType";
import {
  ORDER_TRACKING_STAGE_TABS,
  OrderTrackingStage,
} from "../../../models/Dashboard/OrderTracking/OrderTrackingAnalytics";
import {
  defaultOrderTrackingAnalyticsFilters,
  OrderTrackingAnalyticsFilters,
  parseQueryParamsForOrderTrackingAnalyticsFilters,
  toOrderTypeFilterValues,
} from "../../../models/Dashboard/OrderTracking/OrderTrackingAnalyticsFilters";
import OrderPipelineTable from "../../components/tables/OrderPipelineTable";
import { listingStatusTabsStyles } from "../../../styles/listingStatusTabsSx";
import { OrderTrackingAnalyticsStore } from "../../../stores/OrderTrackingAnalyticsStore";

const STAGE_THEME: Record<
  OrderTrackingStage,
  { color: string; bg: string; border: string }
> = {
  all: { color: "#334155", bg: "#F1F5F9", border: "#CBD5E1" },
  project_pending: { color: "#4F46E5", bg: "#EEF2FF", border: "#C7D2FE" },
  pr_pending: { color: "#7C3AED", bg: "#F5F3FF", border: "#DDD6FE" },
  raw_inward_pending: { color: "#D97706", bg: "#FFFBEB", border: "#FDE68A" },
  print_outward_pending: { color: "#DB2777", bg: "#FDF2F8", border: "#FBCFE8" },
  print_inward_pending: { color: "#0D9488", bg: "#F0FDFA", border: "#99F6E4" },
  dispatch_pending: { color: "#059669", bg: "#ECFDF5", border: "#A7F3D0" },
};

const TAT_METRICS = [
  { key: "tat_project_approval_hours", label: "Project Approval" },
  { key: "tat_mockup_approval_hours", label: "Mockup Approval" },
  { key: "tat_pr_from_project_hours", label: "PR from Project" },
  { key: "tat_raw_inward_from_project_hours", label: "Raw Inward from Project" },
  { key: "tat_print_inward_from_project_hours", label: "Print Inward from Project" },
  { key: "tat_print_inward_from_raw_inward_hours", label: "Print Inward from Raw Inward" },
  { key: "tat_total_project_to_ship_hours", label: "Project to Ship" },
] as const;

const formatCount = (count: number) => (count > 999 ? "999+" : String(count));

const TAT_CARD_HEIGHT = 75;

const FilterField: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <Box>
    <CustomTypography
      variant="heading8"
      sx={{ mb: 0.75, fontSize: "0.72rem", color: "#5C6785", fontWeight: 600 }}
    >
      {label}
    </CustomTypography>
    {children}
  </Box>
);

const OrderTrackingAnalyticsDashboard: React.FC = () => {
  const theme = useTheme();
  const { userStore, projectStore, orderStore, customerStore, TaskTrackerStore } =
    useStores();
  const [orderTrackingAnalyticsStore] = useState(
    () => new OrderTrackingAnalyticsStore(),
  );
  const { showError } = useNotification();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isFiltersExpanded, setIsFiltersExpanded] = React.useState(true);
  const adminSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const orderSearchTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const loading = orderTrackingAnalyticsStore.loading;
  const filters = orderTrackingAnalyticsStore.filters;
  const stageSummary = orderTrackingAnalyticsStore.stageSummary;
  const averages = orderTrackingAnalyticsStore.averages;

  const stageCountMap = useMemo(() => {
    const map = new Map<OrderTrackingStage, number>();
    stageSummary.forEach((item) => map.set(item.stage, item.count));
    return map;
  }, [stageSummary]);

  const activeStageMeta = useMemo(
    () => ORDER_TRACKING_STAGE_TABS.find((tab) => tab.id === filters.stage),
    [filters.stage],
  );

  const fetchData = useCallback(
    async (currentFilters: OrderTrackingAnalyticsFilters) => {
      orderTrackingAnalyticsStore.setLoading(true);
      orderTrackingAnalyticsStore
        .fetchDashboardData(currentFilters)
        .catch((error) => {
          showError(
            "Error",
            error?.message || "Failed to fetch order tracking analytics",
          );
        })
        .finally(() => {
          orderTrackingAnalyticsStore.setLoading(false);
        });
    },
    [orderTrackingAnalyticsStore],
  );

  useEffect(() => {
    if (!userStore.hasPermission([EmployeeRoles.VIEW_OPS_DASHBOARD])) {
      return;
    }

    const init = async () => {
      await projectStore.getTypeOfOrder();
      const hydratedFilters =
        parseQueryParamsForOrderTrackingAnalyticsFilters(searchParams);
      const hasOrderTypeInUrl = Boolean(searchParams.get("order_type_id")?.trim());
      const allOrderTypes = toOrderTypeFilterValues(projectStore.orderType ?? []);
      const filtersWithOrderTypes: OrderTrackingAnalyticsFilters = {
        ...hydratedFilters,
        order_type_id: hasOrderTypeInUrl
          ? hydratedFilters.order_type_id
          : allOrderTypes,
      };
      orderTrackingAnalyticsStore.setFilters(filtersWithOrderTypes);
      void fetchData(filtersWithOrderTypes);
    };

    void init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const nextParams = new URLSearchParams();
    nextParams.set("start_date", filters.start_date.format("YYYY-MM-DD"));
    nextParams.set("end_date", filters.end_date.format("YYYY-MM-DD"));
    nextParams.set("stage", filters.stage);
    nextParams.set("page_no", String(filters.page_no));
    nextParams.set("limit", String(filters.limit));
    nextParams.set("sort_by", filters.sort_by);
    nextParams.set("sort_type", filters.sort_type);

    if (filters.order_type_id.length > 0) {
      nextParams.set(
        "order_type_id",
        filters.order_type_id.map((item) => item.id).join(","),
      );
    }
    if (filters.order_id.length > 0) {
      nextParams.set("order_id", filters.order_id.map((item) => item.id).join(","));
    }
    if (filters.sales_poc_id.length > 0) {
      nextParams.set(
        "sales_poc_id",
        filters.sales_poc_id.map((item) => item.id).join(","),
      );
    }
    if (filters.pm_poc_id.length > 0) {
      nextParams.set("pm_poc_id", filters.pm_poc_id.map((item) => item.id).join(","));
    }
    if (filters.customer_id.length > 0) {
      nextParams.set(
        "customer_id",
        filters.customer_id.map((item) => item.id).join(","),
      );
    }

    setSearchParams(nextParams, { replace: true });
  }, [filters, setSearchParams]);

  const orderTypeOptions = useMemo(
    () =>
      (projectStore.orderType ?? []).map((item) => ({
        id: String(item.id),
        value: item.value,
      })),
    [projectStore.orderType],
  );

  const adminOptions = useMemo(
    () =>
      (TaskTrackerStore.admins ?? []).map((admin) => ({
        id: String(admin.id),
        value: admin.name,
      })),
    [TaskTrackerStore.admins],
  );

  const orderOptions = useMemo(
    () =>
      (orderStore.orderIds ?? []).map((item) => ({
        id: String(item),
        value: String(item),
      })),
    [orderStore.orderIds],
  );

  const customerOptions = useMemo(
    () =>
      (customerStore.customers ?? []).map((customer) => ({
        id: String(customer.id),
        value: customer.name ?? String(customer.id),
      })),
    [customerStore.customers],
  );

  const handleAdminSearch = (searchText?: string) => {
    if (adminSearchTimerRef.current) clearTimeout(adminSearchTimerRef.current);
    if (searchText && searchText.length > 2) {
      adminSearchTimerRef.current = setTimeout(() => {
        TaskTrackerStore.getAdminBySearch(searchText).catch(() => { });
      }, 500);
    }
  };

  const handleOrderSearch = (searchText?: string) => {
    if (orderSearchTimerRef.current) clearTimeout(orderSearchTimerRef.current);
    if (searchText && searchText.length > 0) {
      orderSearchTimerRef.current = setTimeout(() => {
        orderStore.searchOrderIds(searchText).catch(() => { });
      }, 500);
    }
  };

  const handleCustomerSearch = (searchText?: string) => {
    if (searchText && searchText.length > 2) {
      customerStore
        .getCustomers({
          limit: 30,
          page_no: 1,
          search_text: searchText,
          sort_by: "name",
          sort_type: SortType.ASC,
        })
        .catch(() => { });
    }
  };

  const handleStageTabChange = (_event: React.SyntheticEvent, newStage: string) => {
    if (newStage === filters.stage) return;
    const next = {
      ...filters,
      stage: newStage as OrderTrackingStage,
      page_no: 1,
    };
    orderTrackingAnalyticsStore.setFilters(next);
    void fetchData(next);
  };

  const downloadCsv = () => {
    const rows = orderTrackingAnalyticsStore.pipelineRows;
    if (rows.length === 0) {
      showError("Error", "No data to export.");
      return;
    }

    const headers = [
      "Order ID",
      "Order Created",
      "Project Created",
      "Order Type",
      "Customer",
      "Sales POC",
      "PM POC",
      "Product ID",
      "Product",
      "Ordered Qty",
      "Customisation Required",
      "Mockups Approved",
      "Last Mockup Approved",
      "Last PR Date",
      "PR Qty",
      "PO Qty",
      "First Raw Inward",
      "Last Raw Inward",
      "Raw Inward Received Qty",
      "Raw Inward Completed",
      "Print Outward Sent Qty",
      "Printers",
      "Last Print Inward",
      "Print Inward Received Qty",
      "Print Inward Completed",
      "Shipments",
      "First Shipped",
      "TAT Project Approval (hrs)",
      "TAT Mockup Approval (hrs)",
      "TAT PR from Project (hrs)",
      "TAT Raw Inward from Project (hrs)",
      "TAT Print Inward from Project (hrs)",
      "TAT Print Inward from Raw Inward (hrs)",
      "TAT Project to Ship (hrs)",
    ];

    const escapeCsvValue = (value: string | number) =>
      `"${String(value ?? "").replace(/"/g, '""')}"`;

    const formatCsvDate = (value: string | null) => {
      if (!value) return "-";
      const parsed = dayjs(value);
      return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "-";
    };

    const formatYesNo = (value: number) => (value === 1 ? "Yes" : "No");

    const csvRows = rows.map((row) =>
      [
        row.order_id,
        formatCsvDate(row.order_created_date),
        formatCsvDate(row.project_created_date),
        row.order_type || "-",
        row.customer_name || "-",
        row.sales_poc || "-",
        row.pm_poc || "-",
        row.product_id,
        row.product_name || "-",
        row.ordered_quantity,
        formatYesNo(row.customisation_required),
        formatYesNo(row.all_mockups_approved),
        formatCsvDate(row.last_mockup_approved_date),
        formatCsvDate(row.last_pr_date),
        row.pr_quantity,
        row.po_quantity,
        formatCsvDate(row.first_raw_inward_date),
        formatCsvDate(row.last_raw_inward_date),
        row.ri_total_received_quantity - row.ri_total_rejected_quantity,
        formatYesNo(row.raw_inward_completed),
        row.po_total_quantity_sent,
        row.printers || "-",
        formatCsvDate(row.last_print_inward_date),
        row.pi_total_received_quantity - row.pi_total_rejected_quantity,
        formatYesNo(row.print_inward_completed),
        row.shipments_count,
        formatCsvDate(row.first_shipped_date),
        row.tat_project_approval_hours,
        row.tat_mockup_approval_hours,
        row.tat_pr_from_project_hours,
        row.tat_raw_inward_from_project_hours,
        row.tat_print_inward_from_project_hours,
        row.tat_print_inward_from_raw_inward_hours,
        row.tat_total_project_to_ship_hours,
      ]
        .map(escapeCsvValue)
        .join(","),
    );

    const blob = new Blob([[headers.join(","), ...csvRows].join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute(
      "download",
      `order_tracking_${filters.stage}_${dayjs().format("YYYY-MM-DD_HH-mm-ss")}.csv`,
    );
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  if (!userStore.hasPermission([EmployeeRoles.VIEW_OPS_DASHBOARD])) {
    return <PermissionBlockLabel />;
  }

  return (
    <>
      <LoaderOverlay loading={loading} />
      <Box sx={{ bgcolor: "#F7F8FC", minHeight: "100vh", py: { xs: 1, md: 2 }, px: { xs: 1, md: 2 } }}>
        <Paper
          sx={{
            p: { xs: 2, md: 3 },
            borderRadius: 3,
            border: "1px solid #E8ECF5",
            boxShadow: "0 8px 24px rgba(30, 42, 68, 0.04)",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2,
            }}
          >
            <Box>
              <CustomTypography variant="heading3" sx={{ color: "#1E2A44" }}>
                Order Tracking Analytics
              </CustomTypography>
              <CustomTypography
                variant="body2"
                sx={{ mt: 0.5, color: "#5C6785", fontSize: "0.82rem" }}
              >
                {filters.start_date.format("DD MMM YYYY")} –{" "}
                {filters.end_date.format("DD MMM YYYY")}
              </CustomTypography>
            </Box>
          </Box>

          <Accordion
            expanded={isFiltersExpanded}
            onChange={(_, expanded) => setIsFiltersExpanded(expanded)}
            sx={{
              borderRadius: "12px !important",
              border: "1px solid #E8ECF5",
              bgcolor: "#FBFCFF",
              boxShadow: "none",
              "&:before": { display: "none" },
              "& .MuiAccordionSummary-root": { minHeight: 44, px: 2 },
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: "#5C6785" }} />}>
              <CustomTypography variant="heading7" sx={{ color: "#1E2A44" }}>
                Search & Filters
              </CustomTypography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2, px: 2 }}>
              <Grid2 container spacing={1.5} columns={12}>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="Project date range">
                    <DateRangePickerNew
                      startDate={filters.start_date}
                      endDate={filters.end_date}
                      onRangeSelect={(range: { startDate: Dayjs | null; endDate: Dayjs | null }) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          start_date: range.startDate ?? prev.start_date,
                          end_date: range.endDate ?? prev.end_date,
                        }))
                      }
                    />
                  </FilterField>
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="Order type">
                    <AppSearchDropdownMulti
                      title="Order type"
                      list={orderTypeOptions}
                      value={filters.order_type_id}
                      local
                      isMultiSelect
                      handleSelect={(selectedItems) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          order_type_id: (selectedItems ?? []).map((item) => ({
                            id: String(item.id),
                            value: item.value,
                          })),
                        }))
                      }
                      inputHeight="37px"
                      borderRadius="4px"
                    />
                  </FilterField>
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="Order ID">
                    <AppSearchDropdownMulti
                      title="Order ID"
                      list={orderOptions}
                      value={filters.order_id}
                      isMultiSelect
                      handleSearch={handleOrderSearch}
                      handleSelect={(selectedItems) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          order_id: (selectedItems ?? []).map((item) => ({
                            id: String(item.id),
                            value: item.value,
                          })),
                        }))
                      }
                      inputHeight="37px"
                      borderRadius="4px"
                    />
                  </FilterField>
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="Sales POC">
                    <AppSearchDropdownMulti
                      title="Sales POC"
                      list={adminOptions}
                      value={filters.sales_poc_id}
                      isMultiSelect
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItems) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          sales_poc_id: (selectedItems ?? []).map((item) => ({
                            id: String(item.id),
                            value: item.value,
                          })),
                        }))
                      }
                      inputHeight="37px"
                      borderRadius="4px"
                    />
                  </FilterField>
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="PM POC">
                    <AppSearchDropdownMulti
                      title="PM POC"
                      list={adminOptions}
                      value={filters.pm_poc_id}
                      isMultiSelect
                      handleSearch={handleAdminSearch}
                      handleSelect={(selectedItems) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          pm_poc_id: (selectedItems ?? []).map((item) => ({
                            id: String(item.id),
                            value: item.value,
                          })),
                        }))
                      }
                      inputHeight="37px"
                      borderRadius="4px"
                    />
                  </FilterField>
                </Grid2>
                <Grid2 size={{ lg: 2, md: 4, sm: 6, xs: 12 }}>
                  <FilterField label="Customer">
                    <AppSearchDropdownMulti
                      title="Customer"
                      list={customerOptions}
                      value={filters.customer_id}
                      isMultiSelect
                      handleSearch={handleCustomerSearch}
                      handleSelect={(selectedItems) =>
                        orderTrackingAnalyticsStore.updateFilters((prev) => ({
                          ...prev,
                          customer_id: (selectedItems ?? []).map((item) => ({
                            id: String(item.id),
                            value: item.value,
                          })),
                        }))
                      }
                      inputHeight="37px"
                      borderRadius="4px"
                    />
                  </FilterField>
                </Grid2>
                <Grid2
                  size={{ xs: 12 }}
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    alignItems: "flex-end",
                    gap: 1,
                    mt: 0.5,
                  }}
                >
                  <AppButton
                    variant="transparentPrimary"
                    label="Clear"
                    onClick={() => {
                      const resetFilters = {
                        ...defaultOrderTrackingAnalyticsFilters(),
                        order_type_id: toOrderTypeFilterValues(
                          projectStore.orderType ?? [],
                        ),
                      };
                      orderTrackingAnalyticsStore.setFilters(resetFilters);
                      void fetchData(resetFilters);
                    }}
                    customStyles={{ minWidth: 96 }}
                  />
                  <AppButton
                    variant="primary"
                    label="Apply"
                    onClick={() => {
                      const next = { ...filters, page_no: 1 };
                      orderTrackingAnalyticsStore.setFilters(next);
                      void fetchData(next);
                    }}
                    customStyles={{ minWidth: 96 }}
                  />
                </Grid2>
              </Grid2>
            </AccordionDetails>
          </Accordion>

          <Paper
            variant="outlined"
            sx={{
              mt: 2.5,
              p: 2,
              borderRadius: 2,
              borderColor: "#E2E8F4",
              bgcolor: "#FBFCFF",
            }}
          >
            <CustomTypography variant="heading7" sx={{ color: "#1E2A44" }}>
              Average TATs (hrs) · {activeStageMeta?.label}
            </CustomTypography>
            <Grid2 container spacing={1.25} columns={12} sx={{ mt: 1, alignItems: "stretch" }}>
              {TAT_METRICS.map((item, index) => {
                const value = Number(averages[item.key as keyof typeof averages] ?? 0);
                const hue = Math.round((index * 360) / TAT_METRICS.length);

                return (
                  <Grid2
                    key={item.key}
                    size={{ xs: 6, sm: 4, md: 3, lg: 2 }}
                    sx={{ display: "flex" }}
                  >
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.15,
                        width: "100%",
                        height: TAT_CARD_HEIGHT,
                        minHeight: TAT_CARD_HEIGHT,
                        maxHeight: TAT_CARD_HEIGHT,
                        boxSizing: "border-box",
                        borderRadius: 2,
                        borderColor: "#DDE3F5",
                        bgcolor: "#FFFFFF",
                        borderLeft: `3px solid hsl(${hue}, 55%, 58%)`,
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                      }}
                    >
                      <CustomTypography
                        variant="body2"
                        sx={{
                          fontSize: "0.7rem",
                          color: "#5C6785",
                          lineHeight: 1.25,
                          minHeight: "2.5em",
                          display: "-webkit-box",
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: "vertical",
                          overflow: "hidden",
                        }}
                      >
                        {item.label}
                      </CustomTypography>
                      <CustomTypography
                        variant="heading7"
                        sx={{
                          fontSize: "1.05rem",
                          color: value > 0 ? "#1F2A44" : "#A0A8BC",
                          fontWeight: 700,
                          lineHeight: 1.2,
                        }}
                      >
                        {value.toFixed(2)}
                      </CustomTypography>
                    </Paper>
                  </Grid2>
                );
              })}
            </Grid2>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              mt: 2.5,
              borderRadius: 2,
              borderColor: "#E2E8F4",
            }}
          >
            <Box sx={{ px: { xs: 1.5, md: 2 }, pt: 1.5, pb: 0.5, bgcolor: "#FFFFFF" }}>
              <Tabs
                value={filters.stage}
                onChange={handleStageTabChange}
                variant="scrollable"
                scrollButtons="auto"
                allowScrollButtonsMobile
                sx={{
                  ...listingStatusTabsStyles(theme),
                  minHeight: 48,
                  "& .MuiTab-root": {
                    minHeight: 48,
                    px: 1.5,
                    textTransform: "none",
                  },
                }}
              >
                {ORDER_TRACKING_STAGE_TABS.map((tab) => {
                  const count = stageCountMap.get(tab.id) ?? 0;
                  const palette = STAGE_THEME[tab.id];

                  return (
                    <Tab
                      key={tab.id}
                      value={tab.id}
                      label={
                        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
                          <Box component="span" sx={{ whiteSpace: "nowrap" }}>
                            {tab.label}
                          </Box>
                          <Chip
                            label={formatCount(count)}
                            size="small"
                            sx={{
                              height: 20,
                              minWidth: 28,
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              bgcolor: palette.bg,
                              color: palette.color,
                              border: `1px solid ${palette.border}`,
                            }}
                          />
                        </Box>
                      }
                    />
                  );
                })}
              </Tabs>
            </Box>

            <Divider />

            <Box
              sx={{
                px: { xs: 1.5, md: 2 },
                py: 1.5,
                display: "flex",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1.5,
                bgcolor: "#FAFBFE",
                borderBottom: "1px solid #E8ECF5",
              }}
            >
              <Box sx={{ minWidth: 0 }}>
                <CustomTypography
                  variant="heading6"
                  sx={{ color: "#1E2A44", fontSize: "1rem", lineHeight: 1.3 }}
                >
                  {activeStageMeta?.label}
                </CustomTypography>
                <CustomTypography
                  variant="body2"
                  sx={{ fontSize: "0.76rem", color: "#5C6785", mt: 0.35 }}
                >
                  Showing {orderTrackingAnalyticsStore.pipelineRows.length} of{" "}
                  {orderTrackingAnalyticsStore.totalCount} line items
                </CustomTypography>
              </Box>
              <AppButton
                variant="primary"
                label="Download CSV"
                startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                onClick={downloadCsv}
                customStyles={{ minWidth: 148, flexShrink: 0 }}
              />
            </Box>

            <Box sx={{ p: { xs: 1, md: 1.5 }, bgcolor: "#FFFFFF", overflowX: "auto" }}>
              <OrderPipelineTable
                rows={orderTrackingAnalyticsStore.pipelineRows}
                page_no={filters.page_no}
                limit={orderTrackingAnalyticsStore.limit}
                total_count={orderTrackingAnalyticsStore.totalCount}
                sortBy={filters.sort_by}
                sortType={filters.sort_type}
                onSortChange={(column, sortType) => {
                  const next = { ...filters, sort_by: column, sort_type: sortType, page_no: 1 };
                  orderTrackingAnalyticsStore.setFilters(next);
                  void fetchData(next);
                }}
                onPageChange={(_event, page) => {
                  const next = { ...filters, page_no: page };
                  orderTrackingAnalyticsStore.setFilters(next);
                  void fetchData(next);
                }}
                onRowsPerPageChange={(limit) => {
                  const next = { ...filters, limit, page_no: 1 };
                  orderTrackingAnalyticsStore.setFilters(next);
                  void fetchData(next);
                }}
              />
            </Box>
          </Paper>
        </Paper>
      </Box>
    </>
  );
};

export default observer(OrderTrackingAnalyticsDashboard);
